/**
 * A built draft's plan as the plan DOCUMENT (`entities/plan-draft`'s `readPlanDocument`), read once
 * per plan.
 *
 * The draft card lists its sessions off the document and the session panel edits it, on every
 * render of a thread that may hold several drafts. The wire plan is the same object until the
 * conversation is read again, so the reading is kept against it; a plan the builder's reader
 * refuses (a later backend's shape, a day with several sessions) is `null`, and the card then
 * lists no sessions rather than a wrong one.
 *
 * ── A slot with no catalogue exercise yet is read under the name the trainer asked for ───────
 * The draft's plan carries such a slot with exercise id 0 and NO name (measured against the real
 * backend, 2 October 2026): the name lives on the draft's `unresolved` entry, with the slot tokens
 * it stands for. The plan document has one place for an exercise's name, so the name is put on the
 * slot before the plan is read, and the session panel shows „Incline Bench Press" rather than a
 * card with no title. It is display only: an edit sends no exercise names.
 */

import type { AiBuildResult } from '~/entities/ai-conversation';
import { readPlanDocument, type PlanDraft } from '~/entities/plan-draft';

/** The plan with every not-yet-decided slot named as its `unresolved` entry names it. */
function withUnresolvedNames(result: AiBuildResult): AiBuildResult['plan'] {
  const names = new Map(result.unresolved.flatMap((entry) => entry.slotTokens.map((token) => [token, entry.name] as const)));
  if (names.size === 0) return result.plan;
  return {
    ...result.plan,
    phases: (result.plan.phases ?? []).map((phase) => ({
      ...phase,
      days: (phase.days ?? []).map((day) => ({
        ...day,
        sessions: (day.sessions ?? []).map((session) => ({
          ...session,
          exercises: (session.exercises ?? []).map((exercise) => {
            const name = names.get(exercise.slotToken ?? '');
            return name === undefined || (exercise.exerciseName ?? '').trim() !== '' ? exercise : { ...exercise, exerciseName: name };
          }),
        })),
      })),
    })),
  };
}

const documents = new WeakMap<AiBuildResult['plan'], PlanDraft | null>();

export function draftDocument(result: AiBuildResult): PlanDraft | null {
  const known = documents.get(result.plan);
  if (known !== undefined) return known;
  const read = readPlanDocument(withUnresolvedNames(result));
  const draft = read.outcome === 'draft' ? read.draft : null;
  documents.set(result.plan, draft);
  return draft;
}
